export type AdminPageId =
  | 'transportation'
  | 'buildings'
  | 'waste'
  | 'people'
  | 'ghg_inventory'
  | 'policy_tool'
  | 'statewide_analysis';

export interface AdminChartConfig {
  id: string;
  label: string;
  overviewDefault?: boolean;
}

export interface AdminSectionConfig {
  id: string;
  label: string;
  charts: AdminChartConfig[];
}

export interface AdminPageConfig {
  id: AdminPageId;
  label: string;
  path: string;
  sections: AdminSectionConfig[];
}

export const ADMIN_PAGES: AdminPageConfig[] = [
  {
    id: 'transportation',
    label: 'Transportation',
    path: 'transportation',
    sections: [
      {
        id: 'vmt',
        label: 'Vehicle Miles Traveled (VMT)',
        charts: [
          { id: 'vehicle_miles', label: 'Total VMT', overviewDefault: true },
          { id: 'vmt_percapita', label: 'Per Capita' },
          { id: 'vmt_comparison', label: 'VMT Comparison' }
        ]
      },
      {
        id: 'commute_and_departure',
        label: 'Commute & departure to work',
        charts: [
          { id: 'commute_time', label: 'Commute time', overviewDefault: true },
          { id: 'departure_time', label: 'Departure time to work' }
        ]
      },
      {
        id: 'means_of_transportation',
        label: 'Means of Transportation to Work',
        charts: [{ id: 'mode_share', label: 'Mode Share', overviewDefault: true }]
      },
      {
        id: 'transportation_burden',
        label: 'Transportation Burden',
        charts: [{ id: 'transportation_burden_metrics', label: 'Burden & Cost', overviewDefault: true }]
      },
      {
        id: 'walkability',
        label: 'Walkability',
        charts: [
          { id: 'walkability_tract', label: 'Tract map & classification', overviewDefault: true },
          { id: 'walkability_score', label: 'Walk Score' }
        ]
      },
      {
        id: 'ev_adoption',
        label: 'Electric Vehicle Adoption',
        charts: [
          { id: 'ev_fleet_adoption', label: 'EV Fleet Adoption', overviewDefault: true },
          { id: 'ev_per_capita', label: 'Per 1,000 Residents' },
          { id: 'ev_map', label: 'Tract Map' }
        ]
      },
      {
        id: 'ev_charging',
        label: 'EV Charging Infrastructure',
        charts: [
          { id: 'ev_charging_people', label: 'People per Charger' },
          { id: 'ev_charging_counts', label: 'Total Chargers' },
          { id: 'ev_charging_evs', label: 'EVs per Charger' },
          { id: 'ev_charging_map', label: 'Tract Map' }
        ]
      },
      {
        id: 'ev_adoption_barriers',
        label: 'EV Adoption Barriers',
        charts: [{ id: 'ev_adoption_barriers', label: 'Barriers by Income Quintile' }]
      },
      {
        id: 'transportation_maps',
        label: 'Transportation Maps',
        charts: [
          { id: 'ev_adoption', label: 'EV Adoption' },
          { id: 'total_chargers', label: 'Total Chargers' },
          { id: 'vehicles_per_household', label: 'Vehicles per Household' },
          { id: 'zero_vehicle_households', label: 'Zero Vehicle Households' },
          { id: 'commute_time', label: 'Commute Time' }
        ]
      }
    ]
  },
  {
    id: 'buildings',
    label: 'Buildings & Energy',
    path: 'buildings',
    sections: [
      {
        id: 'electricity_consumption',
        label: 'Electricity Consumption',
        charts: [
          { id: 'electricity_consumption', label: 'Total (Stacked)', overviewDefault: true },
          { id: 'elec_res', label: 'Residential' },
          { id: 'elec_com', label: 'Commercial' },
          { id: 'elec_percapita', label: 'Residential Per Capita' }
        ]
      },
      {
        id: 'carbon_intensity',
        label: 'Carbon Intensity of Electricity',
        charts: [{ id: 'carbon_intensity', label: 'Carbon Intensity' }]
      },
      {
        id: 'natural_gas_consumption',
        label: 'Natural Gas Consumption',
        charts: [
          { id: 'natural_gas_consumption', label: 'Total (Stacked)', overviewDefault: true },
          { id: 'gas_res', label: 'Residential' },
          { id: 'gas_com', label: 'Commercial' },
          { id: 'gas_percapita', label: 'Residential Per Capita' }
        ]
      },
      {
        id: 'solar_pv',
        label: 'Solar PV Installations',
        charts: [
          { id: 'solar_capacity', label: 'Capacity' },
          { id: 'cumulative_solar_capacity', label: 'Cumulative Capacity', overviewDefault: true },
          { id: 'solar_per_capita', label: 'Per Capita Comparison' },
          { id: 'solar_cost', label: 'Cost' },
          { id: 'solar_battery_cost', label: 'Battery Cost' },
          { id: 'solar_installers_capacity', label: 'Top Installer Pairs (Capacity)' },
          { id: 'solar_installers_cost', label: 'Top Installer Pairs (Cost)' }
        ]
      },
      {
        id: 'heat_pump',
        label: 'Heat Pump Adoption',
        charts: [
          { id: 'heat_pump_units', label: 'Units Installed' },
          { id: 'heat_pump_per_capita', label: 'Per Capita' },
          { id: 'heat_pump_cost', label: 'Installation Costs' },
          { id: 'heat_pump_technology', label: 'Technology Mix' }
        ]
      },
      {
        id: 'business_establishments',
        label: 'Business Establishments',
        charts: [
          { id: 'business_establishments_sector', label: 'By sector (latest)', overviewDefault: true },
          { id: 'business_establishments_sector_trend', label: 'Sectors over time (stacked)' },
          { id: 'business_establishments_size', label: 'By employment size' }
        ]
      },
      {
        id: 'housing_to_employees',
        label: 'Housing, Population & Employment Trends',
        charts: [{ id: 'energy_housing_to_employees', label: 'Jobs & Housing Balance' }]
      },
      {
        id: 'single_family_detached',
        label: 'Single-Family Detached Homes',
        charts: [{ id: 'single_family_detached', label: 'Single-Family Detached' }]
      },
      {
        id: 'owner_occupied_map',
        label: 'Owner-Occupied Housing Map',
        charts: [{ id: 'owner_occupied_map', label: 'Owner-Occupied Housing' }]
      },
      {
        id: 'urban_heat_island_map',
        label: 'Urban Heat Island Map',
        charts: [{ id: 'urban_heat_island_map', label: 'Urban Heat Island' }]
      },
      {
        id: 'housing_rent_category',
        label: 'Housing Units by Rent Category',
        charts: [
          { id: 'market_rent_line', label: 'Market Rent (Line)' },
          { id: 'market_rent_stacked', label: 'Market Rent (Stacked)' }
        ]
      },
      {
        id: 'affordable_housing_production',
        label: 'Affordable Housing Production',
        charts: [
          {
            id: 'affordable_housing_production_latest',
            label: 'Latest-year units by category',
            overviewDefault: true
          }
        ]
      },
      {
        id: 'bayren_tool',
        label: 'BayREN Tool',
        charts: [{ id: 'bayren_tool', label: 'California BayREN Tool' }]
      }
    ]
  },
  {
    id: 'waste',
    label: 'Waste +',
    path: 'other',
    sections: [
      {
        id: 'waste_management',
        label: 'Waste Management',
        charts: [
          { id: 'waste_tonnage', label: 'Waste Tonnage', overviewDefault: true },
          { id: 'waste_ghg', label: 'Waste GHG Emissions' },
          { id: 'landfill_emissions', label: 'Landfill Emissions', overviewDefault: true }
        ]
      },
      {
        id: 'agriculture',
        label: 'Agriculture',
        charts: [{ id: 'agriculture', label: 'Agriculture Emissions' }]
      },
      {
        id: 'offroad',
        label: 'Offroad Emissions',
        charts: [{ id: 'offroad', label: 'Offroad Emissions' }]
      }
    ]
  },
  {
    id: 'people',
    label: 'People & Equity',
    path: 'people',
    sections: [
      {
        id: 'housing_to_employees',
        label: 'Housing, Population & Employment Trends',
        charts: [{ id: 'people_housing_to_employees', label: 'Jobs & Housing Balance' }]
      },
      {
        id: 'employment',
        label: 'Population & Employment Trends',
        charts: [
          { id: 'employment_total', label: 'Total Employment' },
          { id: 'employment_sex', label: 'Employment by Sex' },
          { id: 'employment_race', label: 'Employment by Race' },
          { id: 'employment_income', label: 'Employment by Income' },
          { id: 'employment_sector', label: 'Employment by Sector' }
        ]
      },
      {
        id: 'housing_burden',
        label: 'Housing Burden',
        charts: [{ id: 'housing_burden', label: 'Housing Burden', overviewDefault: true }]
      },
      {
        id: 'homelessness',
        label: 'Homelessness',
        charts: [
          { id: 'homelessness_rate', label: 'Rate per 1,000 (trend)', overviewDefault: true },
          { id: 'homelessness_gender', label: 'By gender' },
          { id: 'homelessness_race', label: 'By race / ethnicity' },
          { id: 'homelessness_age', label: 'By age' }
        ]
      },
      {
        id: 'crime_safety',
        label: 'Public Safety',
        charts: [
          { id: 'crime_safety_trend', label: 'Rate over time', overviewDefault: true }
        ]
      },
      {
        id: 'health_indicators',
        label: 'Health Indicators',
        charts: [
          { id: 'health_insurance', label: 'Health Insurance' },
          { id: 'health_asthma', label: 'Asthma' },
          { id: 'health_cardiovascular', label: 'Cardiovascular Disease' },
          { id: 'health_low_birth_weight', label: 'Low Birth Weight' }
        ]
      },
      {
        id: 'educational_attainment',
        label: 'Educational Attainment',
        charts: [
          { id: 'education_trends', label: 'Educational Attainment Over Time' },
          { id: 'education_chart', label: 'Educational Attainment (Chart)' },
          { id: 'education_map', label: "Bachelor's Degree or Higher (Map)" }
        ]
      },
      {
        id: 'income_poverty_race',
        label: 'Income and Poverty by Race',
        charts: [
          { id: 'income_by_race', label: 'Income by Race', overviewDefault: true },
          { id: 'income_trends', label: 'Income Trends' },
          { id: 'poverty_trends', label: 'Poverty Trends' },
          { id: 'poverty_bar', label: 'Poverty (Latest)' }
        ]
      },
      {
        id: 'public_assistance',
        label: 'Cash Assistance & SNAP',
        charts: [{ id: 'cash_public_assistance', label: 'Households Receiving Cash Assistance & SNAP' }]
      },
      {
        id: 'neighborhood_opportunity',
        label: 'Neighborhood Opportunity',
        charts: [{ id: 'neighborhood_opportunity', label: 'Neighborhood Opportunity Score' }]
      },
      {
        id: 'economic_sector_wages',
        label: 'Economic Sector Wages',
        charts: [
          { id: 'job_wages_current', label: 'Current Wages' },
          { id: 'job_wages_trends', label: 'Wage Trends' },
          { id: 'job_wages_comparison', label: 'vs California' },
          { id: 'job_wages_median_trend', label: 'Median vs CA/County' }
        ]
      },
      {
        id: 'people_maps',
        label: 'People Maps',
        charts: [
          { id: 'income', label: 'Median Household Income' },
          { id: 'political_lean', label: 'Political Lean' },
          { id: 'population_density', label: 'Population Density' }
        ]
      }
    ]
  },
  {
    id: 'ghg_inventory',
    label: 'GHG Inventory',
    path: 'ghg',
    sections: [
      {
        id: 'sector_based_ghg',
        label: 'Sector-Based GHG Inventory',
        charts: [{ id: 'sector_based_ghg', label: 'Sector-Based GHG Inventory', overviewDefault: true }]
      },
      {
        id: 'consumption_based_ghg',
        label: 'Consumption-Based GHG Inventory',
        charts: [{ id: 'consumption_based_ghg', label: 'Consumption-Based GHG Inventory', overviewDefault: true }]
      },
      {
        id: 'inventory_comparison',
        label: 'Inventory Comparison',
        charts: [{ id: 'inventory_comparison', label: 'Inventory Comparison' }]
      },
      {
        id: 'carbon_footprint_map',
        label: 'Average Household Carbon Footprint Map',
        charts: [{ id: 'carbon_footprint_map', label: 'Carbon Footprint Map' }]
      }
    ]
  },
  {
    id: 'policy_tool',
    label: 'GHG Scenarios',
    path: 'policy-tool',
    sections: [
      {
        id: 'policy_impact_tool',
        label: 'GHG Policy Impact Tool',
        charts: [
          { id: 'policy_ghg_forecast', label: 'GHG Forecast' },
          { id: 'policy_interventions', label: 'Intervention Areas' }
        ]
      }
    ]
  },
  {
    id: 'statewide_analysis',
    label: 'Analysis',
    path: 'analysis',
    sections: [
      {
        id: 'figures',
        label: 'Figures',
        charts: [
          {
            id: 'pub_ca_ghg_stacked',
            label: 'California GHG inventory (stacked by geography)',
            overviewDefault: true,
          },
          { id: 'pub_ca_vs_carb', label: 'Statewide emissions comparison (ClimatePlans vs. CARB)' },
          { id: 'pub_spatial_ghg_map', label: 'Spatial distribution map' },
          { id: 'pub_ghg_rankings', label: 'GHG emissions rankings tables' },
          { id: 'pub_abatement_scatter', label: 'Abatement cost vs. per-capita abatement' },
          {
            id: 'pub_cap_targets_progress',
            label: 'CAP targets vs inventory (met / projected)',
            overviewDefault: true,
          },
        ],
      },
    ],
  },
];

export const getAdminPageConfig = (pageId: AdminPageId): AdminPageConfig | undefined =>
  ADMIN_PAGES.find((page) => page.id === pageId);

export const getAdminChartConfig = (chartId: string): { page: AdminPageConfig; section: AdminSectionConfig; chart: AdminChartConfig } | null => {
  for (const page of ADMIN_PAGES) {
    for (const section of page.sections) {
      const chart = section.charts.find((item) => item.id === chartId);
      if (chart) {
        return { page, section, chart };
      }
    }
  }
  return null;
};
